import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { updatePageMeta, addStructuredData, addBreadcrumbSchema, setJsonLd, removeShellJsonLd } from '../utils/seo';
import { blogPostingSchema, faqSchema, breadcrumbItems } from '../seo/blogSchema';
import { SITE } from '../data/blogConfig';
import { postMeta } from '../seo/routeMeta';
import BlogArticle from '../components/BlogArticle';
import './BlogPost.css';

// Get API URL from environment
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * A public blog post. 2026-10-16: the page now sets its own canonical (it
 * kept the homepage's), names Thomas as the author (Person, linked to /about),
 * sends Cloudinary image URLs in the schema instead of the image object,
 * shows the FAQ it marks up, and follows an approved redirect for a merged
 * post (Pack 2 makes that a server-side 301).
 */
/**
 * The server renderer (api/render.js) prints the post into the raw HTML and
 * hands the same data over in #urp-post, so the first paint is the article
 * rather than a spinner. The page still fetches it (that counts the view and
 * picks up any edit since the render was cached).
 */
function bootstrapPost(slug) {
  try {
    const el = typeof document !== 'undefined' && document.getElementById('urp-post');
    const data = el ? JSON.parse(el.textContent || 'null') : null;
    return data && data.post && data.post.slug === slug ? data : null;
  } catch (e) {
    return null;
  }
}

function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [boot] = useState(() => bootstrapPost(slug));
  const [post, setPost] = useState(boot ? boot.post : null);
  const [relatedPosts, setRelatedPosts] = useState(boot ? boot.relatedPosts || [] : []);
  const [loading, setLoading] = useState(!boot);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!boot || boot.post.slug !== slug) setLoading(true);
        setError(null);
        const response = await axios.get(`${API_URL}/blog/slug/${slug}`);
        if (cancelled) return;
        const p = response.data.post;
        setPost(p);
        setRelatedPosts(response.data.relatedPosts || []);
        if (p) {
          updatePageMeta(postMeta(p));
          removeShellJsonLd(['FAQPage', 'BreadcrumbList']);
          addStructuredData(blogPostingSchema(p));
          setJsonLd('faq-jsonld', faqSchema(p));
          addBreadcrumbSchema(breadcrumbItems(p));
        }
      } catch (err) {
        if (cancelled) return;
        const to = err.response && err.response.data && err.response.data.redirectTo;
        if (to) { navigate(to, { replace: true }); return; }
        // A network blip after the server already rendered this post keeps it.
        const gone = err.response && err.response.status === 404;
        if (boot && boot.post.slug === slug && !gone) return;
        setError('Post not found');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; setJsonLd('faq-jsonld', null); };
  }, [slug, navigate, boot]);

  const handleShare = (platform) => {
    const url = `${SITE}/blog/${post.slug}`;
    const title = post.title;
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      // X (formerly Twitter). Key kept as `twitter` so existing styles apply.
      twitter: `https://x.com/intent/post?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      email: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(url)}`
    };
    if (shareUrls[platform]) window.open(shareUrls[platform], '_blank', 'width=600,height=400');
  };

  if (loading) {
    return (
      <div className="blog-post-loading">
        <div className="spinner"></div>
        <p>Loading post...</p>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="blog-post-error">
        <div className="container">
          <h1>Post Not Found</h1>
          <p>The blog post you're looking for doesn't exist or has been removed.</p>
          <Link to="/blog" className="btn btn-primary">← Back to Blog</Link>
        </div>
      </div>
    );
  }

  return <BlogArticle post={post} relatedPosts={relatedPosts} onShare={handleShare} />;
}

export default BlogPost;
